import { useState, useEffect, useCallback, useMemo } from 'react';
import { registerSW } from 'virtual:pwa-register';

/**
 * Comprehensive PWA hook providing install, offline, update, and standalone status
 * 
 * @returns {Object} PWA state and utilities
 *   - isInstallable: boolean - Can the app be installed
 *   - isInstalled: boolean - Is the app currently installed (standalone mode)
 *   - isOffline: boolean - Is the device currently offline
 *   - isOnline: boolean - Is the device currently online
 *   - hasUpdate: boolean - Is there a service worker update available
 *   - isStandalone: boolean - Is running in standalone/PWA mode
 *   - isIOS: boolean - Is running on iOS
 *   - isAndroid: boolean - Is running on Android
 *   - isMobile: boolean - Is running on mobile device
 *   - install: function - Trigger install prompt
 *   - update: function - Update service worker
 *   - skipWaiting: function - Skip waiting and activate new service worker
 */
export default function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [updateSW, setUpdateSW] = useState(null);
  const [lastUpdateCheckAt, setLastUpdateCheckAt] = useState(null);
  const [updateInProgress, setUpdateInProgress] = useState(false);

  // Detect if running in standalone/PWA mode
  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    // iOS Safari standalone mode
    if (window.navigator.standalone) return true;
    // Android/Chrome standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    // Windows standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
    return false;
  }, []);

  // Detect platform
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iphone|ipad|ipod/i.test(navigator.userAgent || '');
  }, []);

  const isAndroid = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /android/i.test(navigator.userAgent || '');
  }, []);

  const isMobile = useMemo(() => {
    return isIOS || isAndroid || (typeof window !== 'undefined' && window.innerWidth < 768);
  }, [isIOS, isAndroid]);

  // Track install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Track if app is installed (standalone mode)
  useEffect(() => {
    setIsInstalled(isStandalone);
  }, [isStandalone]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Register service worker and track updates
  useEffect(() => {
    if (!import.meta.env.PROD) return;

    const updateServiceWorker = registerSW({
      immediate: true,
      onNeedRefresh() {
        setHasUpdate(true);
      },
      onOfflineReady() {
        // App is ready for offline use
      },
    });

    setUpdateSW(() => updateServiceWorker);
  }, []);

  const checkForUpdates = useCallback(async () => {
    if (!updateSW) return { ok: false, reason: 'no-sw' };
    try {
      setLastUpdateCheckAt(new Date().toISOString());
      // updateSW(false) triggers an update check without forcing activation.
      await updateSW(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.message || 'failed' };
    }
  }, [updateSW]);

  const applyUpdateNow = useCallback(async () => {
    if (!updateSW) return { ok: false, reason: 'no-sw' };

    setUpdateInProgress(true);
    try {
      // Force activation.
      await updateSW(true);

      // Wait for the new SW to take control, then reload.
      if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
        const controlled = Boolean(navigator.serviceWorker.controller);
        if (!controlled) {
          // If there isn't a controller yet, just reload.
          window.location.reload();
          return { ok: true };
        }

        await new Promise((resolve) => {
          let settled = false;
          const timeout = setTimeout(() => {
            if (settled) return;
            settled = true;
            resolve();
          }, 4000);

          navigator.serviceWorker.addEventListener(
            'controllerchange',
            () => {
              if (settled) return;
              settled = true;
              clearTimeout(timeout);
              resolve();
            },
            { once: true }
          );
        });
      }

      setHasUpdate(false);
      window.location.reload();
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.message || 'failed' };
    } finally {
      setUpdateInProgress(false);
    }
  }, [updateSW]);

  const forceRefresh = useCallback(async () => {
    setUpdateInProgress(true);
    try {
      if (typeof caches !== 'undefined' && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // Attempt to clear offline cache if present (localforage-backed).
      try {
        const lf = (await import('localforage')).default;
        if (lf?.clear) {
          await lf.clear();
        }
      } catch (_err) {
        // ignore
      }
      window.location.reload();
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.message || 'failed' };
    } finally {
      setUpdateInProgress(false);
    }
  }, []);

  // Periodic update checks (long-lived PWA sessions).
  useEffect(() => {
    if (!import.meta.env.PROD) return;
    if (!updateSW) return;

    const intervalMs = 30 * 60 * 1000;
    const handle = setInterval(() => {
      checkForUpdates();
    }, intervalMs);

    // First check shortly after boot.
    const kickoff = setTimeout(() => {
      checkForUpdates();
    }, 15 * 1000);

    return () => {
      clearInterval(handle);
      clearTimeout(kickoff);
    };
  }, [checkForUpdates, updateSW]);

  const install = useCallback(async () => {
    if (isIOS && !deferredPrompt) {
      // iOS doesn't support beforeinstallprompt, show instructions
      return {
        success: false,
        message: 'On iOS: Tap Share → "Add to Home Screen"',
        needsManual: true,
      };
    }

    if (!deferredPrompt) {
      return {
        success: false,
        message: 'Install prompt not available',
      };
    }

    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      
      return {
        success: choice.outcome === 'accepted',
        message: choice.outcome === 'accepted' 
          ? 'Installation started' 
          : 'Installation cancelled',
      };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Installation failed',
      };
    }
  }, [deferredPrompt, isIOS]);

  const update = useCallback(async () => {
    const result = await applyUpdateNow();
    return result.ok
      ? { success: true, message: 'Update applied' }
      : { success: false, message: result.reason || 'Update failed' };
  }, [applyUpdateNow]);

  const skipWaiting = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  }, []);

  return {
    isInstallable: Boolean(deferredPrompt) || isIOS,
    isInstalled,
    isOffline,
    isOnline: !isOffline,
    hasUpdate,
    isStandalone,
    isIOS,
    isAndroid,
    isMobile,
    install,
    update,
    skipWaiting,
    // new update helpers
    lastUpdateCheckAt,
    updateInProgress,
    checkForUpdates,
    applyUpdateNow,
    forceRefresh,
  };
}


